import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommentItem, GanttBundle, GanttNode } from '@pathonline/shared';
import { useEffect, useRef, useState } from 'react';
import { getJson, sendJson } from '../api.ts';
import { Composer } from '../comments/Composer.tsx';
import { relTime } from '../comments/model.ts';
import { hubDateTime } from '../format.ts';
import { useEscape } from '../useEscape.ts';
import { canEditNode } from './model.ts';

const enc = encodeURIComponent;

/** What actually went out: all, some (allow-list or failures), or none. */
function mailResult(sent: number, of: number, mode: string): string {
  if (sent === of) return `Emailed to ${of} ${of === 1 ? 'person' : 'people'}.`;
  if (sent > 0) return `Emailed to ${sent} of ${of} people; the others got nothing (mail mode: ${mode}).`;
  return `Recorded for ${of} ${of === 1 ? 'person' : 'people'}, but no email went out (mail mode: ${mode}).`;
}

/** A task's comment thread over the Gantt: read, reply, and (Gantt write) email it to the team. */
export function ThreadModal({ b, node, onClose }: { b: GanttBundle; node: GanttNode; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['views', 'thread', node.id], queryFn: () => getJson<CommentItem[]>(`/api/nodes/${enc(node.id)}/comments`) });
  const [mail, setMail] = useState('');
  const [mailing, setMailing] = useState(false);
  // The wider window; not remembered, as old.
  const [wide, setWide] = useState(false);
  /** The press started on the backdrop (a drag out of the box is not a click on it). */
  const down = useRef(false);
  // A second click while the first is on its way would mail everyone twice.
  const emailThread = () => {
    if (mailing) return;
    setMailing(true);
    setMail('');
    sendJson<{ to: string[]; sent: number; mailMode: string }>('POST', `/api/nodes/${enc(node.id)}/email-thread`)
      .then((r) => setMail(mailResult(r.sent, r.to.length, r.mailMode)))
      .catch((e: Error) => setMail(e.message))
      .finally(() => setMailing(false));
  };

  // Opening the thread reads it: clear its unread mark.
  useEffect(() => {
    void sendJson('POST', '/api/comments/seen', { nodeIds: [node.id] })
      .then(() => qc.invalidateQueries({ queryKey: ['unread'] }))
      .catch(() => undefined);
  }, [node.id, qc]);

  useEscape(onClose);

  const newestFirst = [...(q.data ?? [])].reverse();
  return (
    <div
      className="modal-back"
      role="presentation"
      onMouseDown={(e) => (down.current = e.target === e.currentTarget)}
      onClick={(e) => down.current && e.target === e.currentTarget && onClose()}
    >
      <div className={`modal g-thread ${wide ? 'expanded' : ''}`} role="dialog" aria-modal="true" aria-label={`Comments on ${node.label}`} onClick={(e) => e.stopPropagation()}>
        <div className="g-thread-hd">
          {/* Expand / shrink, as the old ⤡ (DP:7752, 7643; .cmt-modal.expanded DP:6952). */}
          <button type="button" className="g-thread-wide" aria-pressed={wide} onClick={() => setWide(!wide)} title={wide ? 'Shrink' : 'Expand'} aria-label={wide ? 'Shrink the window' : 'Expand the window'}>⤡</button>
          <button type="button" className="panel-close" onClick={onClose} aria-label="Close">×</button>
          <p className="panel-kicker">Comments · {node.id}</p>
          <h2 title={node.label}>{node.label}</h2>
        </div>
        <div className="g-thread-body">
          {b.canComment && <Composer nodeId={node.id} label={node.label} canSetStatus={canEditNode(b, node)} autoFocus />}
          {b.canEdit && (
            <p className="small">
              <button type="button" className="link" disabled={mailing} onClick={emailThread}>
                {mailing ? '✉ Sending…' : '✉ Email this thread to the team'}
              </button>{' '}
              <span className="muted">{mail}</span>
            </p>
          )}
          {q.isPending && <p className="muted">Loading…</p>}
          {q.isError && <p className="error">{q.error.message}</p>}
          {q.data?.length === 0 && <p className="muted small">No comments yet.</p>}
          {newestFirst.map((c) => (
            <div key={c.id} className="t-item">
              <div className="t-head">
                <span className="t-when" title={relTime(c.at)}>{hubDateTime(c.at)}</span>
                <strong>{c.author}</strong>
                {c.status && <span className={`pill s-${c.status.toLowerCase()}`}>→ {c.status}</span>}
              </div>
              {c.text && <p className="t-text">{c.text}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
